/* eslint-disable no-return-assign */

'use client'

import { useRef } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useAutoSlider } from '@hooks'
import { Image } from '@media-components'
import {
  SectionHeader,
  SectionWrapper,
  type CardData,
  type SectionHeaderData,
} from '@components/common'
import { SCROLL_ID } from '@utils/scroll'
import { publicImages } from '@utils/shared'
import { commonStyles, getStyles } from '@utils/styles'
import type { ForceAny } from '@utils/typescript'
import defaultStyles from './styles'

export type CreditSystemData = {
  sectionHeaderData: SectionHeaderData
  creditCardsData: CardData[]
}

export function CreditSystem({
  creditCardsData,
  sectionHeaderData,
}: CreditSystemData) {
  const styles = getStyles(defaultStyles)

  const cardRefs = useRef<ForceAny[]>([])
  const containerRef = useRef<ForceAny>(null)

  const { selected: selectedCard } = useAutoSlider({
    itemsLength: creditCardsData.length,
    autoSlideInterval: 3000,
    mobileOnly: false,
    containerRef,
    cardRefs,
  })

  return (
    <SectionWrapper alternateBg>
      <Stack gap={4} id={SCROLL_ID.CREDIT_SYSTEM}>
        <SectionHeader {...sectionHeaderData} darkBg />

        <Box sx={commonStyles.flexSpace()}>
          <Stack gap={2.5} ref={containerRef}>
            {creditCardsData.map((item, index) => {
              const isSelected = selectedCard === index

              return (
                <Stack
                  key={index}
                  gap={1}
                  ref={(el: ForceAny) =>
                    (cardRefs.current[index] = el as ForceAny)
                  }
                  data-index={index}
                  {...styles('cardWrapper', {
                    ...(isSelected && styles('cardSelected').sx),
                    backgroundColor: isSelected
                      ? 'custom.cardHighlight'
                      : 'custom.creditCardBackground',
                  })}
                >
                  <Typography variant="h6.600" color="text.primaryDark">
                    {item.title}
                  </Typography>

                  <Typography variant="body2.500" color="text.secondaryDark">
                    {item.description}
                  </Typography>

                  <Button
                    variant="contained"
                    startIcon={
                      <Image
                        src={publicImages.coin}
                        alt="coin"
                        sx={{ width: 24, height: 24 }}
                      />
                    }
                    sx={{
                      maxWidth: 'fit-content',
                      backgroundColor: 'background.paperDark',
                      color: 'text.primaryDark',
                      marginTop: '0.25rem',
                    }}
                  >
                    {item.actionButtonData?.children}
                  </Button>
                </Stack>
              )
            })}
          </Stack>

          <Image
            src={publicImages.manOnCoin}
            alt="man-on-coin"
            sx={{
              width: '736px',
              display: {
                xs: 'none',
                sm: 'block',
              },
            }}
            style={{
              width: '100%',
              height: '516px',
            }}
          />
        </Box>
      </Stack>
    </SectionWrapper>
  )
}
